import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { Circuit, PlayerTrackPosition } from '@multirx/shared/types/track.js';
import type { BoardCellType, BoardLayout } from '@multirx/shared/types/board.js';
import { getCorePath } from '@multirx/shared/game/circuitEdit.js';
import { coveredOffsets } from '@multirx/shared/game/circuitRoadPieceEdit.js';
import { getPlayerTrackCell } from '@multirx/shared/game/jokerLap.js';
import {
  buildTrackWaypointsToTarget,
  getTrackForwardDelta,
  trackMovementOccupancyKey,
  trackPositionsEqual,
} from '@multirx/shared/game/trackMovement.js';
import {
  enqueueMotion,
  LIVE_CAR_CELL_MS,
  sampleMotion,
  type CarMotion,
  type MotionEase,
  type MotionPose,
} from './liveCarMotion.js';
import { resolveCar3dModelUrl } from '../cars/car3dModelMap.js';
import { normalizeCarMaterials } from '../cars/car3dMaterial.js';
import { closeTooltipFade, openTooltipFade } from '../ui/tooltipFade.js';
import {
  cacheBustCarGlbUrl,
  carModelSlugFromUrl,
  computeCarOrientationFix,
  facingRotationY,
  liveCarForwardRotationY,
  singleCellCornerCarYawOffsetRadians,
} from '../cars/carOrientationFix.js';
import {
  type CellOccupant,
  layoutsForOccupantsOnCell,
  occupantsOnOccupancyKey,
} from '../game/sharedCellLayout.js';
import { facingStepsToForward } from './liveCarLaneOffset.js';
import { proceduralCornerArmFromSlug } from './proceduralRoadPieces.js';
import {
  buildTrack3dTiles,
  buildJokerLap3dTiles,
  buildStartLane3dTiles,
  type Track3dTile,
} from './track3dLayout.js';
import {
  arcAdjustedGridPosition,
  arcTravelForward,
  type CornerArcInfo,
} from './cornerArcPlacement.js';
import {
  TILE_SIZE,
  computeTrackSceneBounds,
  loadFreeTrackSceneDecor,
  loadModel,
  loadTrackSceneDecor,
  loadTrackSceneGrass,
  loadTrackSceneRoad,
  setupTrackSceneAtmosphere,
} from './circuit3dTrackScene.js';
import {
  mountRaceSkyAtmosphere,
  raceSkyCameraFar,
  type RaceSkyAtmosphereHandle,
} from './circuit3dRaceSky.js';

/** Voiture réelle d'une course en cours (POC : anime la position live sur le circuit 3D). */
export interface LivePlayer3D {
  id: number;
  carName: string;
  /** Modèle 3D assigné explicitement dans la bibliothèque, s'il y en a un. */
  model3dPath?: string | null;
  position: PlayerTrackPosition;
  /** Ordre d'arrivée sur la case (tie-break côte à côte, aligné pions 2D). */
  arrivalStamp?: number;
}

/**
 * `free` : caméra orbitale classique, sans cible imposée.
 * `me` : suit la voiture du pilote humain (`getMyPlayerId`), orbite libre autour.
 * `active` : suit la voiture dont c'est le tour (`getActivePlayerId`), orbite libre autour.
 * Dans les deux modes suivi, l'utilisateur garde entièrement la main sur l'angle et le
 * zoom (`OrbitControls` reste actif) — seul le point suivi se déplace avec la voiture.
 */
export type CameraFollowMode = 'free' | 'me' | 'active';

/** Options de `movePlayerTo` (live + replay archive). */
export type MovePlayerToOptions = {
  distributeTotal?: boolean;
  ease?: MotionEase;
  /**
   * N’appelle pas `cellsToward` : enfile uniquement `position`.
   * Obligatoire en replay case-à-case (sinon wrap circuit si previous désync).
   */
  direct?: boolean;
  /** Téléport immédiat + purge de la file d’anim. */
  snap?: boolean;
};

export interface Circuit3dPreviewHandle {
  dispose(): void;
  /**
   * Résolu quand la route (et le décor) + les voitures live sont chargées
   * (échecs individuels ignorés). Utile pour un swap sans flash bleu.
   */
  ready: Promise<void>;
  /** Anime une voiture "live" (course en cours) vers sa nouvelle case, en `durationMs`. */
  movePlayerTo?(
    playerId: number,
    position: PlayerTrackPosition,
    durationMs: number,
    options?: MovePlayerToOptions
  ): void;
  /** Vide la file d’animation 3D d’un joueur (replay / reset). */
  clearPlayerMotion?(playerId: number): void;
  /** Retire une voiture arrivée (plus affichée, plus d’occupation de case). */
  removePlayer?(playerId: number): void;
  /** Change le mode de suivi caméra (voir `CameraFollowMode`). Sans effet si `interactive: false`. */
  setCameraMode?(mode: CameraFollowMode): void;
  /** Badge HUD permanent au-dessus d’une voiture live (`null` pour retirer). */
  setCarHudBadge?(playerId: number, html: string | null): void;
  /** Vide tous les badges HUD. */
  clearCarHudBadges?(): void;
}

export interface Circuit3dPreviewOptions {
  /** HTML de la carte profil (pilote + voiture) à afficher au survol d'une voiture live. */
  getTooltipHtml?(playerId: number): string;
  /**
   * `false` pour une vignette statique (listes) : pas d'`OrbitControls` (évite
   * qu'un glisser sur la carte soit pris pour une orbite plutôt qu'un défilement
   * ou un clic de navigation), pas de raycasting de survol, pas d'ombres (perf
   * quand plusieurs vignettes sont montées en même temps). Défaut `true`.
   */
  interactive?: boolean;
  /** Mode caméra initial (défaut `'free'`). */
  initialCameraMode?: CameraFollowMode;
  /**
   * Zoom caméra (> 1 = plus près des voitures). Appliqué une fois au montage
   * en rapprochant la caméra de la cible ; le suivi conserve ensuite la distance.
   */
  cameraZoom?: number;
  /** Résout l'identifiant du joueur "moi" pour le mode caméra `'me'` (rappelé chaque frame). */
  getMyPlayerId?(): number | null;
  /** Résout l'identifiant du joueur actif pour le mode caméra `'active'` (rappelé chaque frame). */
  getActivePlayerId?(): number | null;
  /** Type de case du plateau par index de `circuit.path` — pose un marqueur
   * coloré au sol par case (tour de chauffe). Couche dynamique séparée de
   * `cellStyles`/`applySurfaceFill` (bakés dans le mesh du circuit, sans
   * rapport avec le layout plateau généré par meeting). */
  boardLayout?: BoardLayout;
  /** Nom d’affichage du circuit (ex. « Hell ») — module le correctif yaw virage 1-case. */
  circuitName?: string | null;
  /**
   * Ciel dégradé + soleil visible + nuages plats procéduraux.
   * Réservé à la vue Jouer / course (`DiceUI`) — pas les aperçus liste,
   * garage, éditeur, archives.
   */
  raceSky?: boolean;
}

/** Couleur du marqueur au sol par type de case plateau. */
const BOARD_CELL_COLORS: Record<BoardCellType, number> = {
  upgradeMeeting: 0x2ecc71,
  malusMeeting: 0xe74c3c,
  upgradeCarPermanent: 0x3498db,
  upgradeDriverPermanent: 0x9b59b6,
  jokerSeasonUpgrade: 0xf1c40f,
  jokerMeetingUpgrade: 0xf39c12,
  randomCard: 0x1abc9c,
  carPurchase: 0xe67e22,
  casino: 0xff1493,
  bonusPoints: 0x2ecc71,
  malusPoints: 0xe74c3c,
};

const CAR_MODEL_BASE = '/assets/cars/models3d';
/** Nombre de modèles `car_trello_XXX.glb` disponibles (reconstructions 3D de la
 * bibliothèque de voitures) — sert à piocher les voitures de démo de l'aperçu
 * de circuit sans course en cours (voir plus bas), à la place des quelques
 * modèles fixes du Car Kit Kenney utilisés auparavant. */
const CAR_TRELLO_MODEL_COUNT = 121;
/** Cap indépendant du nombre de modèles disponibles : une poignée de voitures
 * de démo suffit à peupler l'aperçu, en charger plus n'apporterait rien de
 * plus tout en alourdissant chaque vignette de liste. */
const MAX_DEMO_CARS = 4;

/** Pioche une URL de modèle `car_trello_XXX.glb` au hasard, en évitant les
 * doublons entre les voitures de démo d'un même aperçu tant que possible. */
function randomCarTrelloModelUrl(excluded: Set<string>): string {
  let slug = '';
  for (let attempt = 0; attempt < CAR_TRELLO_MODEL_COUNT; attempt++) {
    const idx = 1 + Math.floor(Math.random() * CAR_TRELLO_MODEL_COUNT);
    slug = `car_trello_${String(idx).padStart(3, '0')}`;
    if (!excluded.has(slug)) break;
  }
  excluded.add(slug);
  return `${CAR_MODEL_BASE}/${slug}.glb`;
}

/**
 * Position d'une case du tracé principal par rapport à l'arc réel d'un virage
 * (groupé Petit/Moyen/Large, ou virage par défaut 1-case) — voir
 * `buildCornerArcMap` / `cornerArcPlacement.ts` : sans ça, une voiture qui
 * traverse un virage sautait en ligne droite (corde) de centre de case à centre
 * de case au lieu de suivre la courbe réellement affichée
 * (`buildProceduralCorner`, `proceduralRoadPieces.ts`) — d'autant plus visible
 * qu'un virage "Large" balaie large à l'écran alors que le tracé logique n'a
 * qu'un seul pivot avec des bras "tout droit" de part et d'autre.
 */

/** Cadran (est=0/sud=1/ouest=2/nord=3) vers lequel pointe la direction locale
 * `(dx,dz)` une fois tournée de `phi` (même convention que `tileRotationY`). */
function worldCardinalOf(dx: number, dz: number, phi: number): number {
  const wx = dx * Math.cos(phi) + dz * Math.sin(phi);
  const wz = -dx * Math.sin(phi) + dz * Math.cos(phi);
  if (Math.abs(wx) >= Math.abs(wz)) return wx > 0 ? 0 : 2;
  return wz > 0 ? 1 : 3;
}

/** Retrouve la direction d'entrée (`stepIn`) d'un virage depuis sa rotation et
 * sa direction de sortie (`stepOut` = `facingSteps`) — non stockée sur
 * `Track3dTile`, mais déductible : `rotationSteps = turnsRight ? stepIn :
 * (stepIn+1)%4` (voir `track3dLayout.ts#computeTile`), et un virage à droite
 * vérifie `(stepOut-stepIn+4)%4 === 1`. */
function deriveStepIn(rotationSteps: number, stepOut: number): number {
  const stepInIfRight = rotationSteps;
  if ((stepOut - stepInIfRight + 4) % 4 === 1) return stepInIfRight;
  return (rotationSteps + 3) % 4;
}

/**
 * Une entrée par case de virage (pivot ET tout son bras, groupé ou non) —
 * calculée une fois par circuit, pas à chaque déplacement. Les cases de ligne
 * droite/départ/tour joker n'y figurent pas (pas d'ajustement pour elles).
 */
function buildCornerArcMap(circuit: Circuit, tiles: Track3dTile[]): Map<number, CornerArcInfo> {
  const map = new Map<number, CornerArcInfo>();
  const coreLen = getCorePath(circuit.path).length;
  if (coreLen === 0) return map;

  for (const piece of circuit.roadPieces ?? []) {
    if (piece.source !== 'path' || (piece.shape ?? 'straight') !== 'corner') continue;
    const pivotTile = tiles[piece.startIndex];
    if (!pivotTile) continue;
    const offsets = coveredOffsets(piece);
    const armCells = (offsets.length - 1) / 2;
    const phi = -(pivotTile.rotationSteps * Math.PI) / 2;
    const stepIn = deriveStepIn(pivotTile.rotationSteps, pivotTile.facingSteps);
    const entryCardinal = (stepIn + 2) % 4;
    const zeroIsEntry = worldCardinalOf(0, 1, phi) === entryCardinal;
    const info = (offset: number): CornerArcInfo => ({
      pivotX: pivotTile.x,
      pivotY: pivotTile.y,
      rotationSteps: pivotTile.rotationSteps,
      armCells,
      offset,
      zeroIsEntry,
    });
    for (const o of offsets) {
      const coreIdx = (((piece.startIndex + o) % coreLen) + coreLen) % coreLen;
      map.set(coreIdx, info(o));
      // Case de clôture dupliquée d'une boucle fermée (même case que l'index 0,
      // même règle que `track3dLayout.ts#applyMainPathRoadPieceSlugs`).
      if (coreIdx === 0 && tiles.length > coreLen) map.set(coreLen, info(o));
    }
  }
  // Virages non groupés (1 case, sans bras) : ancrés au milieu de leur propre
  // petit arc plutôt qu'au centre de case — lisse aussi ce cas préexistant.
  // `zeroIsEntry` est dérivé comme pour les virages groupés (sinon la tangente
  // d’arc peut pointer à l’envers). Le yaw voiture live n’ajoute +180° que si
  // cette tangente s’oppose encore au sens de circulation (ex. Green Land).
  for (let i = 0; i < tiles.length; i++) {
    if (map.has(i)) continue;
    const tile = tiles[i];
    if (tile.kind !== 'corner') continue;
    const phi = -(tile.rotationSteps * Math.PI) / 2;
    const stepIn = deriveStepIn(tile.rotationSteps, tile.facingSteps);
    const entryCardinal = (stepIn + 2) % 4;
    const zeroIsEntry = worldCardinalOf(0, 1, phi) === entryCardinal;
    map.set(i, {
      pivotX: tile.x,
      pivotY: tile.y,
      rotationSteps: tile.rotationSteps,
      armCells: 0,
      offset: 0,
      zeroIsEntry,
    });
  }
  return map;
}

/**
 * Résout la case 3D (position + orientation) d'une position de course en cours.
 * Ligne de départ, tracé principal et tour joker utilisent tous `facingSteps`
 * (sens de circulation), pas `rotationSteps` (rotation de la pièce de route) — les
 * deux diffèrent sur un virage. La rotation des voitures live suit toutefois la
 * tangente d’arc (`arcTravelForward`) pour rester côte à côte en virage.
 * Sur le tracé principal, une case de virage (groupé ou pas) est repositionnée
 * sur l'arc réel (voir `cornerArcMap`) — ligne de départ et tour joker gardent
 * le centre de case brut pour l'instant (les virages y sont plus rares,
 * amélioration future).
 */
function resolveLivePosition(
  circuit: Circuit,
  tiles: Track3dTile[],
  jokerTiles: Track3dTile[],
  startLaneTiles: Track3dTile[],
  cornerArcMap: Map<number, CornerArcInfo>,
  position: PlayerTrackPosition
): { x: number; y: number; facingSteps: number } | null {
  if (position.startLaneIndex == null && position.jokerBranchIndex == null) {
    const tile = tiles[position.mainPathIndex];
    if (tile) {
      const arc = cornerArcMap.get(position.mainPathIndex);
      const { x, y } = arc ? arcAdjustedGridPosition(arc) : tile;
      return { x, y, facingSteps: tile.facingSteps };
    }
  } else {
    const tile =
      position.startLaneIndex != null
        ? startLaneTiles[position.startLaneIndex]
        : jokerTiles[position.jokerBranchIndex!];
    if (tile) return { x: tile.x, y: tile.y, facingSteps: tile.facingSteps };
  }
  const cell = getPlayerTrackCell(circuit, position);
  if (!cell) return null;
  return { x: cell.x, y: cell.y, facingSteps: 0 };
}

/** Monte une vue 3D orbitale du circuit (tracé, bordures, décor de départ, voitures) — POC three.js. */
export function mountCircuit3dPreview(
  container: HTMLElement,
  circuit: Circuit,
  players?: LivePlayer3D[],
  options?: Circuit3dPreviewOptions
): Circuit3dPreviewHandle {
  const width = container.clientWidth || 480;
  const height = container.clientHeight || 360;
  const interactive = options?.interactive ?? true;
  const circuitName = options?.circuitName ?? null;

  const scene = new THREE.Scene();

  const tiles = buildTrack3dTiles(circuit);
  const jokerTiles = buildJokerLap3dTiles(circuit);
  const startLaneTiles = buildStartLane3dTiles(circuit);
  const cornerArcMap = buildCornerArcMap(circuit, tiles);
  const allTiles = [...tiles, ...jokerTiles, ...startLaneTiles];
  const bounds = computeTrackSceneBounds(allTiles);
  const { centerX, centerZ, span } = bounds;

  const raceSkyEnabled = options?.raceSky === true;
  const cameraFar = raceSkyEnabled ? raceSkyCameraFar(bounds, 500) : 500;
  const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, cameraFar);
  camera.position.set(centerX + span * 0.7, span * 0.85, centerZ + span * 0.9);

  const cameraZoom = options?.cameraZoom != null && options.cameraZoom > 0 ? options.cameraZoom : 1;
  if (cameraZoom !== 1) {
    const lookTarget = new THREE.Vector3(centerX, 0, centerZ);
    camera.position
      .sub(lookTarget)
      .multiplyScalar(1 / cameraZoom)
      .add(lookTarget);
  }

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(width, height);
  renderer.shadowMap.enabled = interactive;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.domElement.className = 'circuit-detail-3d-canvas';
  container.appendChild(renderer.domElement);

  const controls = interactive ? new OrbitControls(camera, renderer.domElement) : null;
  if (controls) {
    controls.target.set(centerX, 0, centerZ);
    controls.maxPolarAngle = Math.PI * 0.49;
    controls.minDistance = Math.max(1.5, span * 0.05) / cameraZoom;
    controls.maxDistance = (span * 3) / cameraZoom;
    // Glisser du clic droit = pan (bouton par défaut d'`OrbitControls`) — mais son
    // axe vertical (haut/bas de la souris) déplace la caméra le long de son axe
    // local "haut", qui bascule fortement une fois la vue inclinée et l'envoie
    // facilement dans le ciel ou sous le sol (repéré en testant). Panoramique natif
    // désactivé au profit d'un pan manuel gauche/droite uniquement (voir
    // `panCameraHorizontal` plus bas), qui ignore totalement le glisser vertical.
    controls.mouseButtons.RIGHT = null;
    controls.update();
  } else {
    // Vignette statique (pas d'OrbitControls) : `controls.update()` est ce qui
    // oriente normalement la caméra vers sa cible — sans lui, la caméra garde
    // sa rotation par défaut et ne cadre jamais le tracé.
    camera.lookAt(centerX, 0, centerZ);
  }

  let disposed = false;
  let frameId = 0;
  let cameraMode: CameraFollowMode = options?.initialCameraMode ?? 'free';
  let raceSky: RaceSkyAtmosphereHandle | null = null;

  setupTrackSceneAtmosphere(scene, bounds);
  if (raceSkyEnabled) {
    raceSky = mountRaceSkyAtmosphere(scene, bounds, {
      seedKey: circuitName ?? 'race',
    });
  }
  loadTrackSceneGrass(scene, bounds, () => disposed);
  const roadReady = loadTrackSceneRoad(scene, allTiles, () => disposed).catch(() => undefined);
  const decorReady = loadTrackSceneDecor(scene, circuit, () => disposed).catch(() => undefined);
  const freeDecorReady = loadFreeTrackSceneDecor(scene, circuit, () => disposed).catch(
    () => undefined
  );
  const carReadyList: Promise<void>[] = [];

  if (options?.boardLayout) {
    const markerGeometry = new THREE.CircleGeometry(TILE_SIZE * 0.32, 24);
    markerGeometry.rotateX(-Math.PI / 2);
    for (const [indexStr, cellType] of Object.entries(options.boardLayout)) {
      const tile = tiles[Number(indexStr)];
      if (!tile) continue;
      const color = BOARD_CELL_COLORS[cellType] ?? 0xffffff;
      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.75,
        depthWrite: false,
      });
      const marker = new THREE.Mesh(markerGeometry, material);
      marker.position.set((tile.x + 0.5) * TILE_SIZE, 0.03, (tile.y + 0.5) * TILE_SIZE);
      marker.renderOrder = 1;
      scene.add(marker);
    }
  }

  /**
   * Calcule et mémorise (dans `userData`) la hauteur de pose au sol et le décalage
   * d'orientation d'un modèle de voiture, une seule fois par modèle :
   * - `groundY` : les pièces Kenney ont déjà leur origine posée au sol, mais un modèle
   *   importé/généré (ex. Hunyuan 3D) a souvent son origine au centre de sa boîte
   *   englobante — on recalcule après mise à l'échelle pour que la base touche
   *   toujours le sol, quelle que soit l'origine d'export.
   * - `orientationFix` : tous les modèles n'ont pas leur longueur alignée sur le même
   *   axe (les pièces Kenney pointent sur Z, mais certains modèles importés ont leur
   *   plus grande dimension horizontale sur X) — sans correction, ces voitures
   *   pointeraient perpendiculairement à la piste.
   */
  function ensureCarPlacementMeta(
    model: THREE.Object3D,
    modelUrl?: string
  ): {
    groundY: number;
    orientationFix: number;
    baseScale: number;
  } {
    if (model.userData.groundY == null) {
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const scale = (TILE_SIZE * 0.5) / Math.max(size.x, size.z, 0.001);
      model.scale.setScalar(scale);
      model.userData.baseScale = scale;
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      normalizeCarMaterials(model);
      const groundedBox = new THREE.Box3().setFromObject(model);
      model.userData.groundY = -groundedBox.min.y + 0.02;
      const slug = modelUrl ? carModelSlugFromUrl(modelUrl) : undefined;
      model.userData.orientationFix = computeCarOrientationFix(slug);
    }
    return model.userData as { groundY: number; orientationFix: number; baseScale: number };
  }

  function applyCarLaneScale(model: THREE.Object3D, laneScale: number): void {
    const meta = ensureCarPlacementMeta(model);
    model.scale.setScalar(meta.baseScale * laneScale);
  }

  function placeCarModel(
    model: THREE.Object3D,
    x: number,
    y: number,
    facingSteps: number,
    laneOffsetX = 0,
    laneOffsetY = 0,
    laneScale = 1,
    modelUrl?: string,
    cornerArmCells: number | null = null,
    heading?: { dx: number; dy: number } | null,
    travelIn?: { dx: number; dy: number } | null,
    travelOut?: { dx: number; dy: number } | null
  ): void {
    const meta = ensureCarPlacementMeta(model, modelUrl);
    applyCarLaneScale(model, laneScale);
    model.position.set(
      (x + 0.5) * TILE_SIZE + laneOffsetX * TILE_SIZE,
      meta.groundY,
      (y + 0.5) * TILE_SIZE + laneOffsetY * TILE_SIZE
    );
    if (heading) {
      model.rotation.y = liveCarForwardRotationY(
        meta.orientationFix,
        heading.dx,
        heading.dy,
        cornerArmCells,
        circuitName,
        { travelIn, travelOut }
      );
    } else {
      model.rotation.y =
        facingRotationY(meta.orientationFix, facingSteps) +
        singleCellCornerCarYawOffsetRadians(cornerArmCells, circuitName);
    }
  }

  const liveCars = new Map<number, THREE.Object3D>();
  const liveCarMotions = new Map<number, CarMotion>();
  // Dernière position connue par joueur : le modèle glTF charge de façon asynchrone,
  // donc on se cale sur la position la plus récente au moment où il apparaît, pas sur
  // celle capturée à l'ouverture de la vue (qui a pu bouger depuis).
  const liveTargetPositions = new Map<number, PlayerTrackPosition>();
  const liveArrivalStamps = new Map<number, number>();

  function resolveWorldPlacement(
    playerId: number,
    position: PlayerTrackPosition
  ): {
    x: number;
    y: number;
    facingSteps: number;
    wx: number;
    wz: number;
    laneScale: number;
    heading: { dx: number; dy: number };
    /** `0` = virage 1-case (correctif yaw conditionnel) ; `null` = hors arc. */
    cornerArmCells: number | null;
    travelIn: { dx: number; dy: number } | null;
    travelOut: { dx: number; dy: number } | null;
  } | null {
    const placement = resolveLivePosition(
      circuit,
      tiles,
      jokerTiles,
      startLaneTiles,
      cornerArcMap,
      position
    );
    if (!placement) return null;
    const occupants: CellOccupant[] = [];
    for (const [id, pos] of liveTargetPositions) {
      occupants.push({
        id,
        stamp: liveArrivalStamps.get(id) ?? 0,
        position: pos,
      });
    }
    const extra: CellOccupant = {
      id: playerId,
      stamp: liveArrivalStamps.get(playerId) ?? 0,
      position,
    };
    const occupancyKey = trackMovementOccupancyKey(circuit, position);
    const onCell = occupantsOnOccupancyKey(circuit, occupants, occupancyKey, extra);
    const leadPos = onCell[0]?.position ?? position;
    const leadArc =
      leadPos.startLaneIndex == null && leadPos.jokerBranchIndex == null
        ? cornerArcMap.get(leadPos.mainPathIndex)
        : undefined;
    const forward = leadArc
      ? arcTravelForward(leadArc)
      : getTrackForwardDelta(circuit, leadPos, {
          // À l’entrée S/F joker : pointer vers la branche si le pion y est / y va.
          enterJokerLap: position.jokerBranchIndex != null,
        });
    const lane = layoutsForOccupantsOnCell(onCell, forward, 1, 'live3d').get(playerId) ?? {
      offsetX: 0,
      offsetY: 0,
      scale: 1,
    };
    const leadTile =
      leadPos.startLaneIndex == null && leadPos.jokerBranchIndex == null
        ? tiles[leadPos.mainPathIndex]
        : null;
    const travelOut = leadTile ? facingStepsToForward(leadTile.facingSteps) : null;
    const travelIn = leadTile
      ? facingStepsToForward(deriveStepIn(leadTile.rotationSteps, leadTile.facingSteps))
      : null;
    // Même vecteur pour l’écart latéral ET le cap : un facingSteps cardinal
    // recouvre les carrosseries au milieu d’un virage (tangente à 45°).
    return {
      ...placement,
      wx: (placement.x + 0.5) * TILE_SIZE + lane.offsetX * TILE_SIZE,
      wz: (placement.y + 0.5) * TILE_SIZE + lane.offsetY * TILE_SIZE,
      laneScale: lane.scale,
      heading: forward,
      cornerArmCells: leadArc ? leadArc.armCells : null,
      travelIn,
      travelOut,
    };
  }

  function poseForPosition(playerId: number, position: PlayerTrackPosition): MotionPose | null {
    const model = liveCars.get(playerId);
    if (!model) return null;
    const world = resolveWorldPlacement(playerId, position);
    if (!world) return null;
    const meta = ensureCarPlacementMeta(model);
    applyCarLaneScale(model, world.laneScale);
    return {
      x: world.wx,
      z: world.wz,
      rotY: liveCarForwardRotationY(
        meta.orientationFix,
        world.heading.dx,
        world.heading.dy,
        world.cornerArmCells,
        circuitName,
        { travelIn: world.travelIn, travelOut: world.travelOut }
      ),
    };
  }

  function enqueueCarPose(
    playerId: number,
    position: PlayerTrackPosition,
    durationMs: number,
    ease?: MotionEase
  ): void {
    const model = liveCars.get(playerId);
    const target = poseForPosition(playerId, position);
    if (!model || !target) return;
    const visual: MotionPose = {
      x: model.position.x,
      z: model.position.z,
      rotY: model.rotation.y,
    };
    const next = enqueueMotion(
      liveCarMotions.get(playerId) ?? null,
      visual,
      target,
      durationMs,
      performance.now(),
      ease ? { ease } : undefined
    );
    if (next) liveCarMotions.set(playerId, next);
  }

  /** Cases intermédiaires entre la position déjà affichée et la cible. */
  function cellsToward(from: PlayerTrackPosition, to: PlayerTrackPosition): PlayerTrackPosition[] {
    if (trackPositionsEqual(from, to)) return [to];
    const budget =
      circuit.path.length +
      (circuit.jokerLap?.cells.length ?? 0) +
      (circuit.startLane?.cells.length ?? 0) +
      4;
    const bridge = buildTrackWaypointsToTarget(circuit, from, to, budget, false, {
      targetJokerLapUsed: to.jokerBranchIndex != null || from.jokerBranchIndex != null,
    });
    return bridge.slice(1);
  }

  if (players && players.length > 0) {
    for (const player of players) {
      liveTargetPositions.set(player.id, player.position);
      liveArrivalStamps.set(player.id, player.arrivalStamp ?? player.id);
      const carModelUrl = cacheBustCarGlbUrl(
        resolveCar3dModelUrl({
          name: player.carName,
          model3dPath: player.model3dPath,
        })
      );
      carReadyList.push(
        loadModel(carModelUrl)
          .then((model) => {
            if (disposed) return;
            const latest = liveTargetPositions.get(player.id) ?? player.position;
            const world = resolveWorldPlacement(player.id, latest);
            if (!world) return;
            const meta = ensureCarPlacementMeta(model, carModelUrl);
            applyCarLaneScale(model, world.laneScale);
            model.position.set(world.wx, meta.groundY, world.wz);
            model.rotation.y = liveCarForwardRotationY(
              meta.orientationFix,
              world.heading.dx,
              world.heading.dy,
              world.cornerArmCells,
              circuitName,
              { travelIn: world.travelIn, travelOut: world.travelOut }
            );
            model.userData.playerId = player.id;
            liveCars.set(player.id, model);
            scene.add(model);
          })
          .catch(() => {
            /* Voiture manquante : sans impact sur l'affichage du tracé (POC). */
          })
      );
    }
  } else if (tiles.length > 0) {
    const carCount = Math.min(MAX_DEMO_CARS, Math.max(1, Math.floor(tiles.length / 10)));
    const step = Math.floor(tiles.length / carCount);
    const usedDemoSlugs = new Set<string>();
    for (let i = 0; i < carCount; i++) {
      const tile = tiles[(i * step) % tiles.length];
      const demoModelUrl = cacheBustCarGlbUrl(randomCarTrelloModelUrl(usedDemoSlugs));
      carReadyList.push(
        loadModel(demoModelUrl)
          .then((model) => {
            if (disposed) return;
            const demoArm =
              tile.kind === 'corner'
                ? ((tile.pieceSlug ? proceduralCornerArmFromSlug(tile.pieceSlug) : null) ?? 0)
                : null;
            const demoIdx = (i * step) % tiles.length;
            const demoArc = cornerArcMap.get(demoIdx);
            const demoHeading = demoArc
              ? arcTravelForward(demoArc)
              : facingStepsToForward(tile.facingSteps);
            const demoTravelOut = facingStepsToForward(tile.facingSteps);
            const demoTravelIn = facingStepsToForward(
              deriveStepIn(tile.rotationSteps, tile.facingSteps)
            );
            placeCarModel(
              model,
              tile.x,
              tile.y,
              tile.facingSteps,
              0,
              0,
              1,
              demoModelUrl,
              demoArm,
              demoHeading,
              demoTravelIn,
              demoTravelOut
            );
            scene.add(model);
          })
          .catch(() => {
            /* Voiture manquante : sans impact sur l'affichage du tracé (POC). */
          })
      );
    }
  }

  const ready = Promise.all([roadReady, decorReady, freeDecorReady, ...carReadyList]).then(
    () => undefined
  );

  // Tooltip (pilote + voiture) au survol d'une voiture live, par raycasting caméra/souris.
  let hoveredPlayerId: number | null = null;
  let carTooltipEl: HTMLDivElement | null = null;
  /** Badges HUD permanents (place / bonus / budget / duel), projetés chaque frame. */
  const carHudBadges = new Map<number, HTMLDivElement>();
  const carHudProjectScratch = new THREE.Vector3();
  const CAR_HUD_WORLD_Y_OFFSET = 0.38;

  function ensureCarHudBadgeEl(playerId: number): HTMLDivElement {
    let el = carHudBadges.get(playerId);
    if (el) return el;
    el = document.createElement('div');
    el.className = 'car-hud-badge-anchor';
    el.hidden = true;
    document.body.appendChild(el);
    carHudBadges.set(playerId, el);
    return el;
  }

  function setCarHudBadge(playerId: number, html: string | null): void {
    if (html == null || html === '') {
      const el = carHudBadges.get(playerId);
      if (el) {
        el.remove();
        carHudBadges.delete(playerId);
      }
      return;
    }
    const el = ensureCarHudBadgeEl(playerId);
    el.innerHTML = html;
  }

  function clearCarHudBadges(): void {
    for (const el of carHudBadges.values()) el.remove();
    carHudBadges.clear();
  }

  function projectCarHudBadges(): void {
    if (carHudBadges.size === 0) return;
    const rect = renderer.domElement.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return;
    for (const [playerId, el] of carHudBadges) {
      const model = liveCars.get(playerId);
      if (!model) {
        el.hidden = true;
        continue;
      }
      carHudProjectScratch.set(
        model.position.x,
        model.position.y + CAR_HUD_WORLD_Y_OFFSET,
        model.position.z
      );
      carHudProjectScratch.project(camera);
      if (carHudProjectScratch.z > 1) {
        el.hidden = true;
        continue;
      }
      const px = (carHudProjectScratch.x * 0.5 + 0.5) * rect.width + rect.left;
      const py = (-carHudProjectScratch.y * 0.5 + 0.5) * rect.height + rect.top;
      el.hidden = false;
      el.style.left = `${Math.round(px)}px`;
      el.style.top = `${Math.round(py)}px`;
    }
  }

  function ensureCarTooltipEl(): HTMLDivElement {
    if (!carTooltipEl) {
      carTooltipEl = document.createElement('div');
      carTooltipEl.className = 'race-profile-tooltip circuit-3d-car-tooltip';
      carTooltipEl.style.position = 'fixed';
      carTooltipEl.style.pointerEvents = 'none';
      carTooltipEl.style.zIndex = '40';
      carTooltipEl.hidden = true;
      document.body.appendChild(carTooltipEl);
    }
    return carTooltipEl;
  }

  function positionCarTooltip(clientX: number, clientY: number): void {
    const tip = ensureCarTooltipEl();
    const pad = 8;
    let left = clientX + 16;
    let top = clientY - tip.offsetHeight - 16;
    if (top < pad) top = clientY + 16;
    left = Math.max(pad, Math.min(left, window.innerWidth - tip.offsetWidth - pad));
    top = Math.max(pad, Math.min(top, window.innerHeight - tip.offsetHeight - pad));
    tip.style.left = `${Math.round(left)}px`;
    tip.style.top = `${Math.round(top)}px`;
  }

  function hideCarTooltip(): void {
    hoveredPlayerId = null;
    if (carTooltipEl) closeTooltipFade(carTooltipEl);
    renderer.domElement.style.cursor = '';
  }

  function findPlayerId(obj: THREE.Object3D | null): number | null {
    for (let cur = obj; cur; cur = cur.parent) {
      if (typeof cur.userData.playerId === 'number') return cur.userData.playerId;
    }
    return null;
  }

  const raycaster = new THREE.Raycaster();
  const pointerNdc = new THREE.Vector2();

  function handleCarPointerMove(e: PointerEvent): void {
    if (!options?.getTooltipHtml || liveCars.size === 0) return;
    const rect = renderer.domElement.getBoundingClientRect();
    pointerNdc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    pointerNdc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointerNdc, camera);
    const hits = raycaster.intersectObjects([...liveCars.values()], true);
    const playerId = hits.length > 0 ? findPlayerId(hits[0].object) : null;

    if (playerId == null) {
      hideCarTooltip();
      return;
    }
    renderer.domElement.style.cursor = 'pointer';
    if (playerId !== hoveredPlayerId) {
      hoveredPlayerId = playerId;
      const tip = ensureCarTooltipEl();
      tip.innerHTML = options.getTooltipHtml(playerId);
      openTooltipFade(tip);
    }
    positionCarTooltip(e.clientX, e.clientY);
  }

  const panAxis = new THREE.Vector3();
  /** Reprend la formule de pan interne d'`OrbitControls` (`_panLeft`), appliquée
   * uniquement à l'axe horizontal (voir le commentaire sur `mouseButtons.RIGHT`
   * plus haut) — jamais de composante verticale, quel que soit le glisser. */
  function panCameraHorizontal(deltaX: number): void {
    if (!controls) return;
    const targetDistance = camera.position.distanceTo(controls.target);
    const distance = (2 * deltaX * targetDistance) / renderer.domElement.clientHeight;
    panAxis.setFromMatrixColumn(camera.matrix, 0);
    panAxis.multiplyScalar(-distance);
    camera.position.add(panAxis);
    controls.target.add(panAxis);
  }

  let panPointerId: number | null = null;
  let panLastX = 0;
  function handlePanPointerDown(e: PointerEvent): void {
    if (e.button !== 2) return;
    panPointerId = e.pointerId;
    panLastX = e.clientX;
  }
  function handlePanPointerMove(e: PointerEvent): void {
    if (panPointerId === null || e.pointerId !== panPointerId) return;
    const deltaX = e.clientX - panLastX;
    panLastX = e.clientX;
    if (deltaX !== 0) panCameraHorizontal(deltaX);
  }
  function handlePanPointerUp(e: PointerEvent): void {
    if (e.pointerId === panPointerId) panPointerId = null;
  }

  if (interactive) {
    renderer.domElement.addEventListener('pointermove', handleCarPointerMove);
    renderer.domElement.addEventListener('pointerleave', hideCarTooltip);
    renderer.domElement.addEventListener('pointerdown', handlePanPointerDown);
    renderer.domElement.addEventListener('pointermove', handlePanPointerMove);
    renderer.domElement.addEventListener('pointerup', handlePanPointerUp);
  }

  function stepLiveCarTweens(now: number): void {
    for (const [playerId, motion] of liveCarMotions) {
      const model = liveCars.get(playerId);
      const stepped = sampleMotion(motion, now);
      if (model) {
        const groundY = ensureCarPlacementMeta(model).groundY;
        model.position.set(stepped.pose.x, groundY, stepped.pose.z);
        model.rotation.y = stepped.pose.rotY;
      }
      if (stepped.motion) liveCarMotions.set(playerId, stepped.motion);
      else liveCarMotions.delete(playerId);
    }
  }

  /** Translate caméra + cible d'`OrbitControls` d'un même vecteur vers la voiture
   * suivie, sans jamais toucher à l'angle/zoom choisis par l'utilisateur — on
   * déplace tout le système orbital plutôt que de recalculer `lookAt`, donc
   * l'orbite libre reste possible pendant le suivi. Lissé (pas de saut) : utile
   * aussi bien à l'activation du mode qu'au changement de voiture suivie
   * (`'active'` change de cible à chaque tour).
   */
  const followDelta = new THREE.Vector3();
  function stepCameraFollow(): void {
    if (cameraMode === 'free' || !controls) return;
    const targetId =
      cameraMode === 'me'
        ? (options?.getMyPlayerId?.() ?? null)
        : (options?.getActivePlayerId?.() ?? null);
    if (targetId == null) return;
    const model = liveCars.get(targetId);
    if (!model) return;
    followDelta.copy(model.position).sub(controls.target);
    if (followDelta.lengthSq() < 0.0001) return;
    followDelta.multiplyScalar(0.08);
    controls.target.add(followDelta);
    camera.position.add(followDelta);
  }

  function animate(): void {
    if (disposed) return;
    stepLiveCarTweens(performance.now());
    stepCameraFollow();
    controls?.update();
    renderer.render(scene, camera);
    projectCarHudBadges();
    frameId = requestAnimationFrame(animate);
  }
  frameId = requestAnimationFrame(animate);

  const resizeObserver = new ResizeObserver(() => {
    const w = container.clientWidth || width;
    const h = container.clientHeight || height;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  });
  resizeObserver.observe(container);

  return {
    dispose(): void {
      if (disposed) return;
      disposed = true;
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      if (interactive) {
        renderer.domElement.removeEventListener('pointermove', handleCarPointerMove);
        renderer.domElement.removeEventListener('pointerleave', hideCarTooltip);
        renderer.domElement.removeEventListener('pointerdown', handlePanPointerDown);
        renderer.domElement.removeEventListener('pointermove', handlePanPointerMove);
        renderer.domElement.removeEventListener('pointerup', handlePanPointerUp);
      }
      carTooltipEl?.remove();
      carTooltipEl = null;
      clearCarHudBadges();
      raceSky?.dispose();
      raceSky = null;
      controls?.dispose();
      scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          const material = obj.material;
          for (const m of Array.isArray(material) ? material : [material]) {
            if (m instanceof THREE.MeshStandardMaterial) m.map?.dispose();
            m.dispose();
          }
        }
      });
      renderer.dispose();
      renderer.domElement.remove();
    },
    ready,
    movePlayerTo(
      playerId: number,
      position: PlayerTrackPosition,
      durationMs: number,
      options?: MovePlayerToOptions
    ): void {
      if (options?.snap) {
        liveCarMotions.delete(playerId);
        liveTargetPositions.set(playerId, position);
        const model = liveCars.get(playerId);
        const pose = poseForPosition(playerId, position);
        if (model && pose) {
          model.position.x = pose.x;
          model.position.z = pose.z;
          model.rotation.y = pose.rotY;
        }
        return;
      }

      const previous = liveTargetPositions.get(playerId);
      const prevKey = previous ? trackMovementOccupancyKey(circuit, previous) : null;
      liveTargetPositions.set(playerId, position);
      const nextKey = trackMovementOccupancyKey(circuit, position);
      const steps = options?.direct
        ? [position]
        : previous
          ? cellsToward(previous, position)
          : [position];
      const ease = options?.ease;
      const perCell =
        options?.distributeTotal && durationMs > 0 && steps.length > 0
          ? durationMs / steps.length
          : steps.length > 1
            ? Math.min(
                220,
                Math.max(
                  LIVE_CAR_CELL_MS,
                  durationMs > 0 ? Math.min(durationMs, 220) : LIVE_CAR_CELL_MS
                )
              )
            : durationMs > 0
              ? durationMs
              : LIVE_CAR_CELL_MS;
      for (const step of steps) enqueueCarPose(playerId, step, perCell, ease);

      // Les rivaux déjà sur l'ancienne ou la nouvelle case se décalent côte à côte.
      if (options?.distributeTotal || options?.direct) return;
      const peerDuration = Math.min(220, Math.max(80, perCell));
      for (const [id, pos] of liveTargetPositions) {
        if (id === playerId) continue;
        const key = trackMovementOccupancyKey(circuit, pos);
        if (!key) continue;
        if (key === prevKey || key === nextKey) {
          enqueueCarPose(id, pos, peerDuration);
        }
      }
    },
    clearPlayerMotion(playerId: number): void {
      liveCarMotions.delete(playerId);
    },
    removePlayer(playerId: number): void {
      const previous = liveTargetPositions.get(playerId);
      const prevKey = previous ? trackMovementOccupancyKey(circuit, previous) : null;
      liveCarMotions.delete(playerId);
      liveTargetPositions.delete(playerId);
      liveArrivalStamps.delete(playerId);
      setCarHudBadge(playerId, null);
      const model = liveCars.get(playerId);
      if (model) {
        scene.remove(model);
        liveCars.delete(playerId);
      }
      if (!prevKey) return;
      const peerDuration = 160;
      for (const [id, pos] of liveTargetPositions) {
        const key = trackMovementOccupancyKey(circuit, pos);
        if (key === prevKey) {
          enqueueCarPose(id, pos, peerDuration);
        }
      }
    },
    setCameraMode(mode: CameraFollowMode): void {
      cameraMode = mode;
    },
    setCarHudBadge,
    clearCarHudBadges,
  };
}
